import { auth } from '@/config/firebase';
import { API_BASE_URL } from '@/config/constants';

function normaliseApiPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';

  const ensuredLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;

  if (!base) return ensuredLeadingSlash;
  if (ensuredLeadingSlash === base) return '';
  if (ensuredLeadingSlash.startsWith(`${base}/`)) {
    return ensuredLeadingSlash.slice(base.length);
  }
  return ensuredLeadingSlash;
}

function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${normaliseApiPath(path)}`;
}

/**
 * Convert a snake_case string to camelCase.
 * Examples: site_address → siteAddress, ai_processing_status → aiProcessingStatus
 */
function toCamel(s: string): string {
  return s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case.
 * Examples: siteAddress → site_address, aiProcessingStatus → ai_processing_status
 */
function toSnake(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

/**
 * Recursively convert all object keys from snake_case to camelCase.
 * Handles nested objects and arrays.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function keysToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(keysToCamel);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toCamel(k), keysToCamel(v)])
    );
  }
  return obj;
}

/**
 * Recursively convert all object keys from camelCase to snake_case.
 * Handles nested objects and arrays.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function keysToSnake(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(keysToSnake);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toSnake(k), keysToSnake(v)])
    );
  }
  return obj;
}

/** Standard API error shape */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public detail?: string,
  ) {
    super(detail ?? `${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * Get the current Firebase ID token for authenticated requests.
 * Returns null if no user is signed in.
 */
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

/**
 * Build request headers including Firebase Bearer token when available.
 * Also includes X-Tenant-Id header when a super-admin tenant override is active.
 */
async function buildHeaders(
  contentType?: string,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  const token = await getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  // Super-admin tenant override (set by TenantProvider)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tenantOverride = (window as any).__tenantOverride;
  if (typeof tenantOverride === 'string' && tenantOverride) {
    headers['X-Tenant-Id'] = tenantOverride;
  }

  return headers;
}

/**
 * Process a fetch response, throwing ApiError on non-2xx status.
 * If a 401 is received, forces a token refresh and retries once.
 */
async function processResponse<T>(
  response: Response,
  retryFn?: () => Promise<Response>,
): Promise<T> {
  if (!response.ok) {
    // On 401, try refreshing the token and retrying once
    if (response.status === 401 && retryFn) {
      const user = auth.currentUser;
      if (user) {
        try {
          await user.getIdToken(true); // force refresh
          const retryResponse = await retryFn();
          return processResponse<T>(retryResponse); // no second retry
        } catch {
          // Token refresh failed — fall through to throw
        }
      }
    }

    let detail: string | undefined;
    try {
      const body = await response.json();
      detail = body.detail ?? body.message ?? JSON.stringify(body);
    } catch {
      detail = response.statusText;
    }
    throw new ApiError(response.status, response.statusText, detail);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const json = await response.json();
  return keysToCamel(json) as T;
}

/**
 * Typed API client with automatic Firebase Bearer token injection
 * and standardised error handling.
 */
export const apiClient = {
  /**
   * GET request.
   */
  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<T> {
    const url = new URL(buildApiUrl(path), window.location.origin);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      }
    }

    const headers = await buildHeaders();
    const doFetch = async () => fetch(url.toString(), {
      method: 'GET',
      headers: await buildHeaders(),
      cache: 'no-store',
    });
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
      cache: 'no-store',
    });

    return processResponse<T>(response, doFetch);
  },

  /**
   * POST request with JSON body.
   */
  async post<T>(path: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders('application/json');
    const jsonBody = body !== undefined ? JSON.stringify(keysToSnake(body)) : undefined;
    const doFetch = async () => fetch(url, {
      method: 'POST',
      headers: await buildHeaders('application/json'),
      body: jsonBody,
      signal: options?.signal,
    });
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: jsonBody,
      signal: options?.signal,
    });

    return processResponse<T>(response, doFetch);
  },

  /**
   * PUT request with JSON body.
   */
  async put<T>(path: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders('application/json');
    const jsonBody = body !== undefined ? JSON.stringify(keysToSnake(body)) : undefined;
    const doFetch = async () => fetch(url, {
      method: 'PUT',
      headers: await buildHeaders('application/json'),
      body: jsonBody,
      signal: options?.signal,
    });
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: jsonBody,
      signal: options?.signal,
    });

    return processResponse<T>(response, doFetch);
  },

  /**
   * PATCH request with JSON body.
   */
  async patch<T>(path: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders('application/json');
    const jsonBody = body !== undefined ? JSON.stringify(keysToSnake(body)) : undefined;
    const doFetch = async () => fetch(url, {
      method: 'PATCH',
      headers: await buildHeaders('application/json'),
      body: jsonBody,
      signal: options?.signal,
    });
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: jsonBody,
      signal: options?.signal,
    });

    return processResponse<T>(response, doFetch);
  },

  /**
   * DELETE request.
   */
  async delete<T = void>(path: string): Promise<T> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders();
    const doFetch = async () => fetch(url, {
      method: 'DELETE',
      headers: await buildHeaders(),
    });
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    return processResponse<T>(response, doFetch);
  },

  /**
   * GET request that returns a raw Blob (for binary downloads like PDFs).
   * Includes Firebase Bearer token authentication.
   */
  async getBlob(path: string): Promise<Blob> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders();
    const doFetch = async () => fetch(url, {
      method: 'GET',
      headers: await buildHeaders(),
      cache: 'no-store',
    });
    const response = await fetch(url, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });

    if (!response.ok) {
      // On 401, try refreshing the token and retrying once
      if (response.status === 401) {
        const user = auth.currentUser;
        if (user) {
          try {
            await user.getIdToken(true); // force refresh
            const retryResponse = await doFetch();
            if (!retryResponse.ok) {
              let detail: string | undefined;
              try {
                const body = await retryResponse.json();
                detail = body.detail ?? body.message ?? JSON.stringify(body);
              } catch {
                detail = retryResponse.statusText;
              }
              throw new ApiError(retryResponse.status, retryResponse.statusText, detail);
            }
            return retryResponse.blob();
          } catch (e) {
            if (e instanceof ApiError) throw e;
            // Token refresh failed — fall through to throw original error
          }
        }
      }

      let detail: string | undefined;
      try {
        const body = await response.json();
        detail = body.detail ?? body.message ?? JSON.stringify(body);
      } catch {
        detail = response.statusText;
      }
      throw new ApiError(response.status, response.statusText, detail);
    }

    return response.blob();
  },

  /**
   * POST request with JSON body, returning a blob response.
   */
  async postBlob(
    path: string,
    body?: unknown,
    options?: { signal?: AbortSignal },
  ): Promise<Blob> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders('application/json');
    const jsonBody = body !== undefined ? JSON.stringify(keysToSnake(body)) : undefined;
    const doFetch = async () => fetch(url, {
      method: 'POST',
      headers: await buildHeaders('application/json'),
      body: jsonBody,
      signal: options?.signal,
    });
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: jsonBody,
      signal: options?.signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        const user = auth.currentUser;
        if (user) {
          try {
            await user.getIdToken(true);
            const retryResponse = await doFetch();
            if (retryResponse.ok) return retryResponse.blob();
          } catch {
            // Fall through to error parsing below.
          }
        }
      }

      let detail: string | undefined;
      try {
        const errorJson = await response.json();
        detail = errorJson.detail ?? errorJson.message ?? JSON.stringify(errorJson);
      } catch {
        detail = response.statusText;
      }
      throw new ApiError(response.status, response.statusText, detail);
    }

    return response.blob();
  },

  /**
   * Upload a file (photo, audio, video) using multipart/form-data.
   */
  async upload<T>(
    path: string,
    file: Blob | Blob[],
    fileName: string | string[],
    additionalFields?: Record<string, string>,
    /** Form field name for the file(s). Defaults to 'file'. */
    fieldName: string = 'file',
  ): Promise<T> {
    const url = buildApiUrl(path);
    const headers = await buildHeaders();
    // Do not set Content-Type — let the browser set it with boundary for FormData
    delete headers['Content-Type'];

    const formData = new FormData();

    if (Array.isArray(file)) {
      const names = Array.isArray(fileName) ? fileName : [];
      file.forEach((f, i) => formData.append(fieldName, f, names[i] || `file_${i}`));
    } else {
      formData.append(fieldName, file, Array.isArray(fileName) ? fileName[0] : fileName);
    }

    if (additionalFields) {
      for (const [key, value] of Object.entries(additionalFields)) {
        formData.append(key, value);
      }
    }

    const doFetch = async () => {
      const retryHeaders = await buildHeaders();
      delete retryHeaders['Content-Type'];
      return fetch(url, {
        method: 'POST',
        headers: retryHeaders,
        body: formData,
      });
    };
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    return processResponse<T>(response, doFetch);
  },
};
